import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import {
  COLOR,
  FONT_SIZE,
  FONT_WEIGHT,
  SPACING,
} from 'constants/design-system';

import { useAppSelector } from 'store/hooks';
import { UserSelectors } from 'store/User';
import { BaseButton, BaseButtonProps } from 'components/BaseButton';

import type { Order } from 'store/Orders/types';
import type { OrderDetailCardState } from './OrderDetailCardStateContainer';

export type OrderDetailCardMenuControlProps = {
  onPressShowHiddenItems?: BaseButtonProps['onPress'];
  onPressExpandComments?: BaseButtonProps['onPress'];
  isCommentExpanded?: boolean;
  isNotificationShown?: boolean;
  isFinancialDetailShown?: boolean;
  hasHiddenItems?: boolean;
  isHiddenItemShown?: boolean;
  isViewEntireOrderButtonHidden?: boolean;
  shouldHideExpandCommentButton?: boolean;
  onPressHideFinancialInfo?: BaseButtonProps['onPress'];
  onPressHideNotifications?: BaseButtonProps['onPress'];
  onPressClose?: BaseButtonProps['onPress'];
  selectedOrderItemUuid: string | undefined;
  onPressViewEntireOrder?: BaseButtonProps['onPress'];
  isAllHeaderOptionVisible?: boolean;
  onPressIsAllHeaderOptionVisible?: BaseButtonProps['onPress'];
};

export const OrderDetailCardMenuControl = (
  props: OrderDetailCardMenuControlProps,
) => {
  const {
    onPressExpandComments,
    onPressHideFinancialInfo,
    onPressHideNotifications,
    onPressShowHiddenItems,
    isCommentExpanded,
    onPressClose,
    shouldHideExpandCommentButton,
    hasHiddenItems,
    isHiddenItemShown,
    selectedOrderItemUuid,
    onPressViewEntireOrder,
    isNotificationShown,
    isFinancialDetailShown,
    isAllHeaderOptionVisible,
    onPressIsAllHeaderOptionVisible,
    isViewEntireOrderButtonHidden,
  } = props;

  const { t } = useTranslation();

  const featureFlags = useAppSelector(UserSelectors.selectFeatureFlags);

  return (
    <View
      style={{
        flexDirection: 'row',
        // padding: SPACING.theme[12],
        gap: SPACING.theme[4],
        alignItems: 'center',
      }}
    >
      <View
        style={{
          flex: 1,
          alignItems: selectedOrderItemUuid ? 'flex-start' : 'center',
        }}
      >
        {selectedOrderItemUuid ? (
          isViewEntireOrderButtonHidden ? null : (
            <BaseButton
              title={t('chef.itemDetails.viewEntireOrder')}
              onPress={onPressViewEntireOrder}
              style={{
                height: 32,
                paddingHorizontal: SPACING.theme[8],
              }}
              startIcon={{
                variant: 'material',
                name: 'arrow-back',
                size: FONT_SIZE.theme.xl,
              }}
              fontSize={FONT_SIZE.theme.sm}
              fontWeight={FONT_WEIGHT.theme.bold}
            />
          )
        ) : hasHiddenItems ? (
          <BaseButton
            title={
              isHiddenItemShown
                ? t('overview.orderDetails.hideHiddenItemsLabel')
                : t('overview.orderDetails.showHiddenItemsLabel')
            }
            color={
              isHiddenItemShown ? COLOR.theme.red['500'] : COLOR.theme.blue[500]
            }
            startIcon={{
              variant: 'material-community',
              name: isHiddenItemShown ? 'eye-off' : 'eye',
              size: FONT_SIZE.theme.xl,
            }}
            onPress={onPressShowHiddenItems}
            fontSize={FONT_SIZE.theme.sm}
            fontWeight={FONT_WEIGHT.theme.bold}
            style={{
              height: 32,
              paddingHorizontal: SPACING.theme[8],
            }}
          />
        ) : null}
      </View>
      <View
        style={{
          flex: 1,
          flexDirection: 'row',
          justifyContent: 'space-between',
          gap: SPACING.theme[4],
          alignItems: 'center',
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            gap: SPACING.theme[12],
            alignItems: 'center',
          }}
        >
          <BaseButton
            startIcon={{
              variant: 'material-community',
              name: isNotificationShown ? 'bell' : 'bell-off',
            }}
            color={isNotificationShown ? undefined : COLOR.theme.red['500']}
            fontSize={FONT_SIZE.theme.xl}
            style={{
              height: 32,
              width: 32,
            }}
            onPress={onPressHideNotifications}
          />
          {featureFlags.display_financial_info && (
            <BaseButton
              startIcon={{
                variant: 'material',
                name: isFinancialDetailShown ? 'attach-money' : 'money-off',
              }}
              color={
                isFinancialDetailShown ? undefined : COLOR.theme.red['500']
              }
              fontSize={FONT_SIZE.theme['2xl']}
              style={{
                height: 32,
                width: 32,
              }}
              onPress={onPressHideFinancialInfo}
            />
          )}
          <BaseButton
            startIcon={{
              variant: 'material-community',
              name: isAllHeaderOptionVisible
                ? 'information'
                : 'information-off',
            }}
            color={
              isAllHeaderOptionVisible ? undefined : COLOR.theme.red['500']
            }
            fontSize={FONT_SIZE.theme.xl}
            style={{
              height: 32,
              width: 32,
            }}
            onPress={onPressIsAllHeaderOptionVisible}
          />
        </View>
        {!shouldHideExpandCommentButton && (
          <BaseButton
            title={
              isCommentExpanded
                ? t('overview.orderDetails.collapseNotesLabel')
                : t('overview.orderDetails.expandNotesLabel')
            }
            startIcon={{
              variant: 'material-community',
              name: 'arrow-expand-all',
              size: FONT_SIZE.theme.xl,
            }}
            numberOfLines={1}
            color={isCommentExpanded ? COLOR.theme.red[500] : undefined}
            fontSize={FONT_SIZE.theme.sm}
            fontWeight={FONT_WEIGHT.theme.bold}
            onPress={onPressExpandComments}
            style={{
              height: 32,
              minWidth: 32,
              paddingHorizontal: SPACING.theme[8],
              flexShrink: 1,
            }}
            disabled={shouldHideExpandCommentButton}
          />
        )}

        <BaseButton
          startIcon={{
            variant: 'material',
            name: 'close',
          }}
          onPress={onPressClose}
          fontSize={FONT_SIZE.theme['2xl']}
          backgroundColor={COLOR.theme.transparent}
          color={COLOR.theme.white}
          style={{
            height: 32,
            width: 32,
          }}
        />
      </View>
    </View>
  );
};

type OrderDetailCardMenuControlForStateContainerProps = Pick<
  OrderDetailCardMenuControlProps,
  | 'hasHiddenItems'
  | 'isHiddenItemShown'
  | 'onPressClose'
  | 'onPressShowHiddenItems'
  | 'isViewEntireOrderButtonHidden'
> & {
  order: Pick<Order, 'special_instructions' | 'internal_notes'>;
} & Pick<
    OrderDetailCardState,
    | 'isCommentExpand'
    | 'selectedOrderItemUuid'
    | 'setSelectedOrderItemUuid'
    | 'setIsCommentExpand'
    | 'setIsFinancialDetailVisible'
    | 'setIsNotificationVisible'
    | 'isFinancialDetailVisible'
    | 'isNotificationVisible'
    | 'isAllHeaderOptionVisible'
    | 'setIsAllHeaderOptionVisible'
  >;

export const OrderDetailCardMenuControlForStateContainer = (
  props: OrderDetailCardMenuControlForStateContainerProps,
) => {
  const {
    isCommentExpand,
    selectedOrderItemUuid,
    setIsCommentExpand,
    setIsFinancialDetailVisible,
    setIsNotificationVisible,
    setSelectedOrderItemUuid,
    hasHiddenItems,
    isHiddenItemShown,
    onPressClose,
    onPressShowHiddenItems,
    order,
    isFinancialDetailVisible,
    isNotificationVisible,
    isAllHeaderOptionVisible,
    setIsAllHeaderOptionVisible,
    isViewEntireOrderButtonHidden,
  } = props;
  return (
    <OrderDetailCardMenuControl
      selectedOrderItemUuid={selectedOrderItemUuid}
      isViewEntireOrderButtonHidden={isViewEntireOrderButtonHidden}
      hasHiddenItems={hasHiddenItems}
      isCommentExpanded={isCommentExpand}
      isHiddenItemShown={isHiddenItemShown}
      isFinancialDetailShown={isFinancialDetailVisible}
      isNotificationShown={isNotificationVisible}
      onPressClose={(e) => {
        onPressClose?.(e);
        setSelectedOrderItemUuid(undefined);
      }}
      onPressExpandComments={() => {
        setIsCommentExpand((prev) => !prev);
      }}
      onPressHideFinancialInfo={() => {
        setIsFinancialDetailVisible((prev) => !prev);
      }}
      onPressHideNotifications={() => {
        setIsNotificationVisible((prev) => !prev);
      }}
      onPressShowHiddenItems={onPressShowHiddenItems}
      onPressViewEntireOrder={() => {
        setSelectedOrderItemUuid(undefined);
      }}
      shouldHideExpandCommentButton={
        isCommentExpand
          ? false
          : (order.special_instructions === '' ||
              order.special_instructions === undefined ||
              order.special_instructions === null) &&
            (order.internal_notes === null ||
              order.internal_notes === undefined ||
              order.internal_notes?.length === 0)
      }
      isAllHeaderOptionVisible={isAllHeaderOptionVisible}
      onPressIsAllHeaderOptionVisible={() => {
        setIsAllHeaderOptionVisible((prev) => !prev);
      }}
    />
  );
};
